import {
  chatWebSocketEnvelopeSchema,
  type ChatMessageSend,
  type ChatWebSocketEnvelope,
} from '@billing/validation';
import { useCurrentUserStore } from 'store/UserStore';
import {
  getWebSocket,
  registerWebSocket,
  subscribeToWebSocketMessages,
} from './session';

export type PresenceMessage =
  | {
      action: 'hello' | 'goodbye';
      userID: string;
      timestamp?: number;
    }
  | { action: 'presence.snapshot'; userIDs: string[] };

export const listenForPresence = (
  _userID: string,
  handler: (message: PresenceMessage) => void
): (() => void) =>
  subscribeToWebSocketMessages((event) => {
    if (typeof event.data !== 'string') return;
    try {
      const parsed = JSON.parse(event.data) as Record<string, unknown>;
      if (
        parsed.action === 'presence.snapshot' &&
        Array.isArray(parsed.userIDs) &&
        parsed.userIDs.every((userID) => typeof userID === 'string')
      ) {
        handler({ action: parsed.action, userIDs: parsed.userIDs as string[] });
        return;
      }
      if (
        (parsed.action === 'hello' || parsed.action === 'goodbye') &&
        typeof parsed.userID === 'string'
      ) {
        handler({
          action: parsed.action,
          userID: parsed.userID,
          timestamp:
            typeof parsed.timestamp === 'number' ? parsed.timestamp : undefined,
        });
      }
    } catch {
      // Ignore payloads owned by other WebSocket routes.
    }
  });

const waitForSocket = (socket: WebSocket): Promise<void> => {
  if (socket.readyState === WebSocket.OPEN) return Promise.resolve();
  if (socket.readyState !== WebSocket.CONNECTING) {
    return Promise.reject(new Error('Chat connection is not available.'));
  }
  return new Promise((resolve, reject) => {
    const timeoutID = window.setTimeout(
      () => reject(new Error('Chat connection timed out.')),
      5000
    );
    socket.addEventListener(
      'open',
      () => {
        window.clearTimeout(timeoutID);
        resolve();
      },
      { once: true }
    );
    socket.addEventListener(
      'error',
      () => {
        window.clearTimeout(timeoutID);
        reject(new Error('Chat connection failed.'));
      },
      { once: true }
    );
  });
};

export const sendChatMessage = async (
  input: Omit<ChatMessageSend, 'action'>
): Promise<void> => {
  const { cognitoID, organizationID } = useCurrentUserStore.getState();
  if (!cognitoID) throw new Error('Chat user is unavailable.');
  const socket =
    getWebSocket() ??
    (await registerWebSocket(cognitoID, organizationID || undefined));
  if (!socket) throw new Error('Unable to start chat session.');
  await waitForSocket(socket);
  await new Promise<void>((resolve, reject) => {
    const timeoutID = window.setTimeout(() => {
      unsubscribe();
      reject(new Error('The chat server did not acknowledge the message.'));
    }, 15_000);
    const unsubscribe = subscribeToChat((event) => {
      if (
        event.action === 'chat.message.created' &&
        event.message.clientMessageID === input.clientMessageID
      ) {
        window.clearTimeout(timeoutID);
        unsubscribe();
        resolve();
      }
      if (
        event.action === 'chat.message.rejected' &&
        event.clientMessageID === input.clientMessageID
      ) {
        window.clearTimeout(timeoutID);
        unsubscribe();
        reject(new Error(event.reason));
      }
    });
    socket.send(JSON.stringify({ action: 'chat.message.send', ...input }));
  });
};

export const subscribeToChat = (
  subscriber: (event: ChatWebSocketEnvelope) => void
): (() => void) =>
  subscribeToWebSocketMessages((event) => {
    if (typeof event.data !== 'string') return;
    try {
      const parsed = chatWebSocketEnvelopeSchema.safeParse(
        JSON.parse(event.data)
      );
      if (parsed.success) subscriber(parsed.data);
    } catch {
      // Other WebSocket routes intentionally use different payloads.
    }
  });
